import React, { useState } from 'react'
import { useForm } from 'react-hook-form'
import { BaseTextInput } from 'components/FormUtils/TextInput/text-input'
import { DateRangePicker } from 'components/FormUtils/DateRangePicker'
import { DatePicker } from 'components/FormUtils/DatePicker'
import { Preview, Section, Col } from 'component-catalog/previews/PreviewKit'

type DateRangeForm = {
  dateRange: { from: Date; to?: Date }
}

// ---------------------------------------------------------------------------
// DatePicker — shows both modes (button-only and allowTyping)
// ---------------------------------------------------------------------------
export function DatePickerMock(): React.ReactElement {
  const [value1, setValue1] = useState<string | undefined>('2025-03-15')
  const [value2, setValue2] = useState<string | undefined>('2025-03-15')
  const [value3, setValue3] = useState<string | undefined>(undefined)
  return (
    <Preview>
      <Section label="Button-only mode (default)">
        <Col>
          <DatePicker label="Due Date" value={value1} onChange={setValue1} />
          <DatePicker
            label="Optional date"
            value={value3}
            onChange={setValue3}
            optional
          />
        </Col>
      </Section>
      <Section label="Typing mode (allowTyping) — type MM/DD/YYYY or click calendar">
        <Col>
          <DatePicker
            label="Start Date"
            value={value2}
            onChange={setValue2}
            allowTyping
          />
          <DatePicker
            label="End Date (empty)"
            value={undefined}
            onChange={() => {}}
            allowTyping
            optional
          />
        </Col>
      </Section>
    </Preview>
  )
}

// ---------------------------------------------------------------------------
// DateRangePicker — requires react-hook-form control prop
// ---------------------------------------------------------------------------
export function DateRangePickerMock(): React.ReactElement {
  const { control } = useForm<DateRangeForm>({
    defaultValues: {
      dateRange: {
        from: new Date(2025, 2, 10),
        to: new Date(2025, 2, 24),
      },
    },
  })
  return (
    <Preview>
      <Section label="Date range selector (react-hook-form controlled)">
        <DateRangePicker
          control={control}
          name="dateRange"
          label="Date Range"
        />
      </Section>
      <Section label="Optional variant">
        <DateRangePicker
          control={control}
          name="dateRange"
          label="Contract Period"
          optional
        />
      </Section>
    </Preview>
  )
}

// ---------------------------------------------------------------------------
// FormUtils — text input variants
// ---------------------------------------------------------------------------
export function FormUtilsMock(): React.ReactElement {
  const [name, setName] = useState('Acme Corp')
  const [amount, setAmount] = useState<string | number>(1500)
  const [phone, setPhone] = useState('')
  const { control } = useForm<DateRangeForm>({
    defaultValues: {
      dateRange: {
        from: new Date(2025, 2, 10),
        to: new Date(2025, 2, 24),
      },
    },
  })

  return (
    <Preview>
      <Section label="Text inputs">
        <Col className="max-w-sm">
          <BaseTextInput
            name="company"
            label="Company Name"
            value={name}
            onChange={(v) => setName(String(v))}
            placeholder="Enter company name"
          />
          <BaseTextInput
            name="amount"
            label="Deal Amount"
            type="number"
            value={amount}
            onChange={(v) => setAmount(v)}
            prefix="$"
          />
          <BaseTextInput
            name="phone"
            label="Phone (empty)"
            value={phone}
            onChange={(v) => setPhone(String(v))}
            placeholder="+1 (555) 000-0000"
            optional
          />
          <BaseTextInput
            name="disabled"
            label="Read-only Field"
            value="Cannot edit this value"
            disabled
          />
        </Col>
      </Section>
      <Section label="Date range picker">
        <DateRangePicker
          control={control}
          name="dateRange"
          label="Date Range"
        />
      </Section>
    </Preview>
  )
}
